import type { DocumentNode } from 'graphql';
import type { GraphQLTaggedNode } from 'react-relay';
import type { Person } from '@atlassian/jira-filters/src/common/types.tsx';
import type { Color } from '@atlassian/jira-issue-epic-color/src/common/types.tsx';
import type {
	IssueId,
	IssueKey,
	IssueTypeId,
	ProjectId,
	ProjectKey,
} from '@atlassian/jira-shared-types/src/general.tsx';

// @ts-expect-error - TS2456 - Type alias 'Partial' circularly references itself. | TS2315 - Type 'Partial' is not generic.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Partial<T> = Partial<Flow.Diff<T, Record<any, any>>>;

/**
 * Deployment
 */
const PRODUCTION = 'PRODUCTION' as const;
const STAGING = 'STAGING' as const;
const TESTING = 'TESTING' as const;
const DEVELOPMENT = 'DEVELOPMENT' as const;
const UNMAPPED = 'UNMAPPED' as const;

export const DeploymentEnvironmentTypeOptions = {
	PRODUCTION,
	STAGING,
	TESTING,
	DEVELOPMENT,
	UNMAPPED,
} as const;

export type DeploymentEnvironmentType =
	(typeof DeploymentEnvironmentTypeOptions)[keyof typeof DeploymentEnvironmentTypeOptions];

export type EnvironmentId = string;
export type EnvironmentName = string;
export type EnvironmentTypeConcatName = string;

export type DeploymentEnvironment = {
	id: EnvironmentId;
	type: DeploymentEnvironmentType;
	displayName: EnvironmentName | EnvironmentTypeConcatName;
};

const PENDING = 'PENDING' as const;
const IN_PROGRESS = 'IN_PROGRESS' as const;
const SUCCESSFUL = 'SUCCESSFUL' as const;
const CANCELLED = 'CANCELLED' as const;
const FAILED = 'FAILED' as const;
const ROLLED_BACK = 'ROLLED_BACK' as const;
const UNKNOWN = 'UNKNOWN' as const;

export const DeploymentStateOptions = {
	PENDING,
	IN_PROGRESS,
	SUCCESSFUL,
	CANCELLED,
	FAILED,
	ROLLED_BACK,
	UNKNOWN,
} as const;

export type DeploymentState = (typeof DeploymentStateOptions)[keyof typeof DeploymentStateOptions];

export type DeploymentPipeline = {
	id: string;
	displayName: string;
	url: string;
};

export type DeploymentId = string;

export type Deployment = {
	id: DeploymentId;
	issueIds: IssueId[];
	deploymentSequenceNumber: number;
	updateSequenceNumber: number;
	displayName: string;
	url: string;
	description: string;
	lastUpdated: Date;
	state: DeploymentState;
	pipeline: DeploymentPipeline;
	environment: DeploymentEnvironment;
};

export type Epic = {
	id: IssueId;
	summary: string;
};

export type EpicLozenge = {
	summary: string;
	color: Color;
};

/**
 * Issue
 */
export type Issue = {
	id: IssueId;
	key: IssueKey;
	typeId: IssueTypeId;
	projectId: ProjectId;
	summary: string;
	assignee: Person | null;
	epic: EpicLozenge | null;
};

export type IssueType = {
	id: IssueTypeId;
	name: string;
	iconUrl: string;
};

export type Project = {
	id: ProjectId;
	key: ProjectKey;
	name: string;
	isSimplified: boolean;
	isProjectAdmin: boolean;
	issueTypes: IssueType[];
};

export type EnvironmentTypeViewConfiguration = {
	production: boolean;
	staging: boolean;
	testing: boolean;
	development: boolean;
	unmapped: boolean;
};

export type MockRequest<RequestVariables> = {
	query: DocumentNode;
	variables: RequestVariables;
};

export type MockRequestRelay<RequestVariables> = {
	query: GraphQLTaggedNode;
	variables: RequestVariables;
};
export type MockResult<ResultData> = {
	data?: ResultData;
	errors?: Error[];
};
export type MockResultRelay<ResultData> = {
	data?: ResultData;
	error?: Error;
};
export type MockResponse<ResultData, RequestVariables> = {
	request: MockRequest<RequestVariables>;
	result: MockResult<ResultData>;
	delay?: number;
};

export type MockResponseRelay<ResultData, RequestVariables> = {
	request: MockRequestRelay<RequestVariables>;
	result: MockResultRelay<ResultData>;
};

export type DatePickerProps = {
	disabledDates?: Date[];
	disableFuture?: boolean;
	disablePast?: boolean;
	minDate?: Date;
	maxDate?: Date;
};
export type DateObj = {
	day: number;
	month: number;
	year: number;
};

export type CalendarEventType = 'left' | 'up' | 'right' | 'down' | 'prev' | 'next';

export type ChangeEvent = {
	iso?: string;
	type: CalendarEventType;
} & DateObj;
